import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router';
import { Search, ChevronLeft, ChevronRight, Receipt } from 'lucide-react';
import { SortHeader, PlainHeader, type SortDir } from '@/components/list/sort-header';
import {
  FilterChip,
  FilterGroup,
  ClearFiltersButton,
} from '@/components/list/filter-chip';
import { formatShortCalendarDate, formatShortDate } from '@/components/list/format-relative';
import { CategoryIcon } from '@/components/category-icon/category-icon';
import { adminFetch } from '@/api';

interface Expense {
  id: string;
  amount: number;
  categoryName: string;
  categoryEmoji: string | null;
  note: string | null;
  date: string;
  createdAt: string;
  deletedAt: string | null;
  creator: { name: string; email: string };
  group: { name: string };
  paymentMethod: { label: string; type: string } | null;
}

type SortKey = 'date' | 'amount' | 'createdAt';
type StatusFilter = 'active' | 'deleted' | 'all';

export default function ExpensesPage() {
  const navigate = useNavigate();
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<SortKey>('date');
  const [dir, setDir] = useState<SortDir>('desc');
  const [status, setStatus] = useState<StatusFilter>('active');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const fetchExpenses = useCallback(async () => {
    const params = new URLSearchParams({
      page: String(page),
      search,
      sort,
      dir,
      status,
    });
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    const res = await adminFetch(`/api/expenses?${params.toString()}`);
    const data = await res.json();
    setExpenses(data.expenses);
    setTotal(data.total);
  }, [page, search, sort, dir, status, from, to]);

  useEffect(() => { fetchExpenses(); }, [fetchExpenses]);

  const totalPages = Math.ceil(total / 20);

  const applySort = (key: SortKey) => {
    if (key === sort) {
      setDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSort(key);
      setDir('desc');
    }
    setPage(1);
  };

  const filtersActive =
    status !== 'active' || search.length > 0 || from !== '' || to !== '';
  const clearFilters = () => {
    setStatus('active');
    setSearch('');
    setFrom('');
    setTo('');
    setPage(1);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-[22px] font-bold text-white tracking-tight">Expenses</h1>
          <p className="text-zinc-500 text-[13px] mt-0.5">{total.toLocaleString('en-IN')} total transactions</p>
        </div>
      </div>

      <div className="flex flex-col gap-3 md:flex-row md:items-center">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-600" />
          <input
            placeholder="Search by note, category, or user..."
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
            className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-white/[0.04] border border-white/[0.06] text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-emerald-500/30 transition-all"
          />
        </div>
        <div className="flex items-center gap-2">
          <input
            type="date"
            value={from}
            onChange={(e) => { setFrom(e.target.value); setPage(1); }}
            className="px-3 py-2.5 rounded-xl bg-white/[0.04] border border-white/[0.06] text-[12px] text-white focus:outline-none focus:border-emerald-500/30"
          />
          <span className="text-zinc-600 text-[12px]">→</span>
          <input
            type="date"
            value={to}
            onChange={(e) => { setTo(e.target.value); setPage(1); }}
            className="px-3 py-2.5 rounded-xl bg-white/[0.04] border border-white/[0.06] text-[12px] text-white focus:outline-none focus:border-emerald-500/30"
          />
        </div>
      </div>

      <FilterGroup label="Status">
        {(['active', 'deleted', 'all'] as const).map((v) => (
          <FilterChip
            key={v}
            label={v.charAt(0).toUpperCase() + v.slice(1)}
            active={status === v}
            onClick={() => { setStatus(v); setPage(1); }}
          />
        ))}
        {filtersActive && <ClearFiltersButton onClick={clearFilters} />}
      </FilterGroup>

      <div className="bg-white/[0.02] border border-white/[0.06] rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/[0.06]">
                <th className="px-5 py-3 text-left"><PlainHeader label="Category" /></th>
                <th className="px-5 py-3 text-left"><PlainHeader label="Note" /></th>
                <th className="px-5 py-3 text-left">
                  <SortHeader label="Amount" sortKey="amount" activeSort={sort} activeDir={dir} onSort={applySort} />
                </th>
                <th className="px-5 py-3 text-left"><PlainHeader label="User" /></th>
                <th className="px-5 py-3 text-left"><PlainHeader label="Group" /></th>
                <th className="px-5 py-3 text-left"><PlainHeader label="Payment" /></th>
                <th className="px-5 py-3 text-left">
                  <SortHeader label="Date" sortKey="date" activeSort={sort} activeDir={dir} onSort={applySort} />
                </th>
                <th className="px-5 py-3 text-left">
                  <SortHeader label="Created" sortKey="createdAt" activeSort={sort} activeDir={dir} onSort={applySort} />
                </th>
              </tr>
            </thead>
            <tbody>
              {expenses.map(exp => (
                <tr
                  key={exp.id}
                  onClick={() => navigate(`/admin/expenses/${exp.id}`)}
                  className="cursor-pointer border-b border-white/[0.03] hover:bg-white/[0.02] transition-colors"
                >
                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-2.5">
                      <CategoryIcon emoji={exp.categoryEmoji} size="sm" />
                      <span className="text-[13px] text-zinc-300">{exp.categoryName}</span>
                    </div>
                  </td>
                  <td className="px-5 py-3.5">
                    <span className={`text-[13px] truncate block max-w-[180px] ${exp.deletedAt ? 'text-red-400/70 line-through' : 'text-zinc-400'}`}>{exp.note || '—'}</span>
                  </td>
                  <td className="px-5 py-3.5">
                    <span className={`text-[13px] font-semibold font-[family-name:var(--font-jetbrains)] ${exp.deletedAt ? 'text-red-400/70' : 'text-white'}`}>
                      Rs.{exp.amount.toLocaleString('en-IN')}
                    </span>
                  </td>
                  <td className="px-5 py-3.5">
                    <div>
                      <p className="text-[12px] text-zinc-300">{exp.creator.name}</p>
                      <p className="text-[10px] text-zinc-600">{exp.creator.email}</p>
                    </div>
                  </td>
                  <td className="px-5 py-3.5">
                    <span className="text-[11px] px-2 py-0.5 rounded-full bg-white/[0.04] text-zinc-400 border border-white/[0.06]">
                      {exp.group.name}
                    </span>
                  </td>
                  <td className="px-5 py-3.5">
                    <span className="text-[11px] text-zinc-500">{exp.paymentMethod?.label || '—'}</span>
                  </td>
                  <td className="px-5 py-3.5">
                    <span className="text-[11px] text-zinc-500 font-[family-name:var(--font-jetbrains)]">
                      {formatShortCalendarDate(exp.date)}
                    </span>
                  </td>
                  <td className="px-5 py-3.5">
                    <span className="text-[11px] text-zinc-600 font-[family-name:var(--font-jetbrains)]">
                      {formatShortDate(exp.createdAt)}
                    </span>
                  </td>
                </tr>
              ))}
              {expenses.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-5 py-16 text-center">
                    <Receipt className="w-8 h-8 text-zinc-700 mx-auto mb-3" />
                    <p className="text-zinc-500 text-sm">No expenses match the current filters.</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-[11px] text-zinc-600 font-[family-name:var(--font-jetbrains)]">
            Page {page} of {totalPages} · {total.toLocaleString('en-IN')} results
          </p>
          <div className="flex gap-1.5">
            <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}
              className="p-2 rounded-lg border border-white/[0.06] text-zinc-400 hover:bg-white/[0.04] disabled:opacity-30 transition-all">
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}
              className="p-2 rounded-lg border border-white/[0.06] text-zinc-400 hover:bg-white/[0.04] disabled:opacity-30 transition-all">
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
